import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
    ButtonGroup,
    ButtonGroupOptionPropType,
} from '@ratehub/base-ui';


// Done this way because the forwardRef() argument cannot have .propTypes, but
// the result of forwardRef() can.
const ButtonGroupContainer = forwardRef((
    {
        options,
        initialTabIndex = 0,
        onChange,
        disabled = false,
        buttonGroupWidth,
        children,
        ...otherProps
    },
    ref, // Allows parents to observe this component for things like dimensions
) => {
    if (!options || !options.length) {
        throw new Error('[ButtonGroupContainer]: options prop is required to exist and have a length');
    }
    if (!React.Children || React.Children.count(children) !== options.length) {
        throw new Error('[ButtonGroupContainer]: children are required and children’s length should match the length of the options prop');
    }

    const [ activeIndex, setActiveIndex ] = useState(initialTabIndex);

    function handleButtonGroupChange(index) {
        setActiveIndex(index);
        onChange?.(index);
    }

    // WARNING: I'm fairly certain the use of React.Children will cause problems
    // inside Gutenberg but only if we try to do certain things, such as
    // advanced block selections & manipulations using Gutenberg's block editor
    // API. Gutenberg will get confused by React.Children and fail to perform as
    // expected. We don't need ButtonGroupContainer to be a block today, though
    // I'm not totally sure how else to do this.

    // If we ever solve this issue, please note that it needs to be solved in
    // our other tab components as well.
    const tabs = React.Children.map(children, child => {
        const tab = {
            title: child?.props?.title ?? '',
            subTitle: child?.props?.subTitle ?? '',
            body: child,
        };

        if (!tab.title && !tab.body) {
            throw new Error('[ButtonGroupContainer] Invalid tab found. Tabs are invalid if they have no `title` or `body`');
        }

        return tab;
    });

    return (
        <div
            ref={ref}
            className="rh-display-flex rh-flex-direction-column"
            {...otherProps}
        >
            <div className="rh-width-100p rh-display-flex rh-justify-content-center rh-mb-2">
                <ButtonGroup
                    options={options}
                    initialTabIndex={initialTabIndex}
                    onChange={handleButtonGroupChange}
                    width={buttonGroupWidth}
                    disabled={disabled}
                />
            </div>

            <ul className="tab-panels rh-height-100p rh-list-style-type-none rh-m-0 rh-p-0">
                <For
                    each="tab"
                    of={tabs}
                    index="index"
                >
                    <li
                        key={`button-group-tab-panel-${index}`}
                        className={classNames(
                            'panel rh-bg-coconut rh-height-100p',
                            {
                                'rh-display-none': index !== activeIndex,
                            },
                        )}
                        role="tabpanel"
                    >
                        {tab.body}
                    </li>
                </For>
            </ul>
        </div>
    );
});


ButtonGroupContainer.propTypes = {
    options: PropTypes.arrayOf(ButtonGroupOptionPropType),
    initialTabIndex: PropTypes.number,
    onChange: PropTypes.func,
    // a CSS length as a string (includes unit e.g. `20rem` or `50%`, or even `min(100%, 450px)`)
    buttonGroupWidth: PropTypes.string,
    disabled: PropTypes.bool,
    children: PropTypes.node,
};

export default ButtonGroupContainer;
